import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Container,
  Alert,
} from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { CompleteRegistration } from '../../interfaces/auth';
import { authService } from '../../services/auth';
import { styles } from './styles';

const CompleteRegistrationPage: React.FC = () => {
  const { completeRegistration, user, loading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<CompleteRegistration>({
    firstName: '',
    lastName: '',
    username: user?.username || '',
  });
  
  const [validationError, setValidationError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  
  // Função para forçar novo login quando o token está inválido
  const forceRelogin = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    navigate('/login');
  };
  
  // Verificar status de autenticação ao carregar a página
  useEffect(() => {
    // Verificar token no localStorage
    const token = localStorage.getItem('auth_token');
    if (!token) {
      // Se não tiver token, força novo login
      setRequestError('Você não está autenticado. Redirecionando para a página de login...');
      setTimeout(() => forceRelogin(), 2000);
      return;
    }
    
    // Verificar usuário no contexto
    if (!user || !user.id) {
      setRequestError('ID de usuário inválido ou ausente. Redirecionando para login...');
      setTimeout(() => forceRelogin(), 2000);
      return;
    }
    
    // Verificar usuário no localStorage
    const localUser = localStorage.getItem('user');
    if (localUser) {
      try {
        const parsedUser = JSON.parse(localUser);
        
        // Verificar se o ID do usuário está no localStorage 
        if (!parsedUser.id) {
          setRequestError('Dados do usuário inválidos. Redirecionando para login...');
          setTimeout(() => forceRelogin(), 2000);
          return;
        }
      } catch (error) {
        setRequestError('Erro ao processar dados do usuário. Redirecionando para login...');
        setTimeout(() => forceRelogin(), 2000);
        return;
      }
    } else {
      setRequestError('Dados do usuário não encontrados. Redirecionando para login...');
      setTimeout(() => forceRelogin(), 2000);
      return;
    }
    
    if (!isAuthenticated) {
      setRequestError('Você não está autenticado corretamente. Redirecionando para login...');
      setTimeout(() => forceRelogin(), 2000);
      return;
    }
    
    // Se chegou até aqui, a autenticação está ok
    // Pré-preencher os campos se o usuário já tiver nome e sobrenome
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      username: user.username || ''
    });
  }, [user, navigate, isAuthenticated]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setRequestError(null);
    
    // Simple validation
    if (!formData.firstName.trim()) {
      setValidationError('Nome é obrigatório');
      return;
    }
    
    if (!formData.lastName.trim()) {
      setValidationError('Sobrenome é obrigatório');
      return;
    }
    
    if (!formData.username?.trim()) {
      setValidationError('Nome de usuário é obrigatório');
      return;
    }
    
    try {
      // Verificar se o token está presente antes de enviar
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setRequestError('Token de autenticação não encontrado. Faça login novamente.');
        setTimeout(() => forceRelogin(), 2000);
        return;
      }
      
      // Usar diretamente o authService ao invés do hook useAuth
      try {
        const result = await authService.completeRegistration(formData);
        
        // Após completar o registro com sucesso, redirecionar para a página de login
        // com uma mensagem informando que o registro foi concluído
        localStorage.setItem('registration_success', 'true');
        
        // Fazer logout antes de redirecionar para a página de login
        await authService.logout();
        
        // Redirecionar para a página de login
        navigate('/login');
      } catch (serviceError: any) {
        throw serviceError; // Re-lançar para ser capturado pelo catch externo
      }
    } catch (err: any) {
      // Capturar detalhes do erro para exibir na interface
      
      let errorMessage = 'Erro desconhecido ao completar o registro';
      
      // Interpretar o erro sem depender de axios.isAxiosError
      if (err.response) {
        // Parece um erro de resposta da API
        const data = err.response.data;
        errorMessage = data.message || data.error || 
                       (data.errors && data.errors.join(', ')) || 
                       `Erro ${err.response.status}: ${err.response.statusText}`;
      } else if (err.message) {
        // Erro genérico com mensagem
        errorMessage = err.message;
      }
      
      setRequestError(errorMessage);
      
      // Se parecer um erro de autorização, redirecionar para login
      if (err.response?.status === 401 || errorMessage.includes('token') || 
          errorMessage.includes('autoriza') || errorMessage.includes('ID de usuário')) {
        setTimeout(() => forceRelogin(), 2000);
      }
    }
  };
  
  return (
    <Container maxWidth="sm" sx={styles.container}>
      <Paper elevation={3} sx={styles.paper}>
        <Box sx={styles.header}>
          <PersonOutline fontSize="large" color="primary" />
          <Typography variant="h5" sx={styles.title}>
            Complete seu Cadastro
          </Typography>
        </Box>
        
        <Typography variant="body1" sx={styles.welcomeText}>
          Bem-vindo(a), {user?.username}! Para continuar, precisamos de algumas informações adicionais.
        </Typography>
        

        
        {requestError && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {requestError}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
          <TextField
            label="Nome de Usuário"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            required
            fullWidth
            margin="normal"
            variant="outlined"
            autoFocus
          />
          
          <TextField
            label="Nome"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            fullWidth
            margin="normal"
            variant="outlined"
          />
          
          <TextField
            label="Sobrenome"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            fullWidth
            margin="normal"
            variant="outlined"
          />
          
          {(validationError || error) && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {validationError || error}
            </Alert>
          )}
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={styles.submitButton}
            disabled={loading || !!requestError}
          >
            {loading ? <CircularProgress size={24} /> : 'Concluir Cadastro'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompleteRegistrationPage;